import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import ResponsiveImg from "../ui/ResponsiveImg";
import { ChevronDown, MapPin, Search } from "lucide-react";
import useAuth from "../../services/useAuth";

const HeroSection = ({ image, mobileImage, title, description, tagline }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api } = useAuth();
  const dropdownRef = useRef(null);

  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 500], ["0%", "20%"]);
  const textOpacity = useTransform(scrollY, [0, 300], [1, 0]);
  const textScale = useTransform(scrollY, [0, 300], [1, 0.9]);

  const [formData, setFormData] = useState({ destination: "", selectedTrek: null });
  const [treks, setTreks] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Hide the search bar on the packages page
  const showSearch = location.pathname !== "/packages";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showSearch) return; // skip fetching treks if search isn't shown
    const fetchTreks = async () => {
      try {
        const response = await api.get("/api/treks/getall");
        setTreks(response.data.data.treks);
      } catch (error) {
        console.error("Failed to fetch treks:", error);
      }
    };
    fetchTreks();
  }, [api, showSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, destination: value }));
    if (value.trim().length > 0) {
      const filtered = treks.filter(t =>
        t.name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchResults(filtered);
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  };

  const handleSelect = (trek) => {
    setFormData({ destination: trek.name, selectedTrek: trek });
    setIsDropdownOpen(false);
    navigate(`/package/${trek._id}`);
  };

  return (
    <section className="relative min-h-[85vh] w-full flex items-center justify-center px-4">

      {/* BACKGROUND */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="h-full w-full">
          {/* The page's largest image (LCP): load it first, at high priority */}
          <ResponsiveImg
            src={image || "/assets/default-himalaya.jpg"}
            mobileSrc={mobileImage}
            className="w-full h-[120%] object-cover scale-105"
            alt="Himalayan Background"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 to-[#C99D89]/40" />
        </motion.div>
      </div>

      <div className="relative z-10 w-full max-w-5xl px-6 flex flex-col items-center text-center">
        <motion.div style={{ opacity: textOpacity, scale: textScale }}>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block text-[#CAC0BE] font-medium tracking-[0.4em] uppercase text-xs md:text-sm mb-4 drop-shadow-md"
          >
            {tagline || "Escape to the Peaks"}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-8xl font-serif text-[#FCFCFC] mb-6 leading-tight drop-shadow-2xl"
          >
            {title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-lg md:text-xl text-[#D9E4EE] font-light max-w-2xl mx-auto mb-12 drop-shadow-md"
          >
            {description}
          </motion.p>
        </motion.div>

        {/* SEARCH — hidden on /packages */}
        {showSearch && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="relative w-full max-w-lg"
          >
            <div className="group flex items-center bg-black/30 backdrop-blur-2xl border border-white/20 rounded-2xl p-2 transition-all focus-within:bg-black/40 focus-within:ring-2 focus-within:ring-[#575C3E]/70 shadow-2xl">
              <Search className="ml-4 text-[#CAC0BE]" size={22} />
              <input
                type="text"
                value={formData.destination}
                onChange={handleSearchChange}
                placeholder="Find your next journey..."
                className="w-full bg-transparent border-none focus:ring-0 text-[#FCFCFC] placeholder:text-gray-400 px-4 py-4 text-lg"
              />
            </div>

            <AnimatePresence>
              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute w-full mt-4 bg-[#575C3E]/95 backdrop-blur-3xl rounded-2xl border border-white/10 overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.6)] z-50"
                >
                  <div className="max-h-[320px] overflow-y-auto py-2">
                    {searchResults.length > 0 ? (
                      searchResults.map((trek) => (
                        <button
                          key={trek._id}
                          onClick={() => handleSelect(trek)}
                          className="w-full flex items-center gap-4 px-6 py-4 text-left transition-all duration-200 hover:bg-white/10 group"
                        >
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#FCFCFC]/20">
                            <MapPin size={18} className="text-[#FCFCFC]" />
                          </div>
                          <div className="flex-grow">
                            <p className="font-medium text-[#FCFCFC]">{trek.name}</p>
                            <p className="text-[10px] uppercase tracking-widest text-[#FCFCFC]/60 mt-1">
                              {trek.difficulty || "Moderate"} • {trek.duration || "7 Days"}
                            </p>
                          </div>
                          <ChevronDown size={18} className="text-white/0 group-hover:text-white/40 -rotate-90 transition-all" />
                        </button>
                      ))
                    ) : (
                      <div className="px-6 py-8 text-center text-[#FCFCFC]/50 text-sm italic">
                        No matching peaks found...
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => navigate('/packages')}
                    className="w-full py-4 bg-black/20 border-t border-white/10 text-[#FCFCFC] text-xs font-bold uppercase tracking-[0.2em] hover:bg-black/40 transition-all flex items-center justify-center gap-2"
                  >
                    View All Expeditions
                    <ChevronDown size={14} className="-rotate-90" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center text-white/60"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] mb-2 drop-shadow-md">Explore</span>
        <ChevronDown size={20} />
      </motion.div>
    </section>
  );
};

export default HeroSection;